import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { autorun } from 'mobx'
import { trackWatchFolderSelected, trackAutoSyncFolderStopped } from 'src/services/tracker/trackers/uploads'
import type { ThunkDispatch } from 'redux-thunk'
import type { AnyAction } from 'redux'
import {
	createFileUploads, removeAssetUploads, updateAssetUploadFile,
	renameUploadedAsset, checkAssetFilenamesMatches
} from 'src/actions/assetUploads'
import type { SyncedFile } from 'src/new_arch/stores/FolderSyncStore'
import type FolderSyncStore from 'src/new_arch/stores/FolderSyncStore'
import FilenamesMatchResult from '@uploads/models/FilenamesMatchResult'
import { ASSET_ALL_EXTENSIONS } from 'src/entities/asset'
import { useApi } from 'src/new_arch/providers/ApiProvider'
import { PROJECTS_URL, FOLDERS_URL } from 'src/config/urls'
import { getRawAssetUploads } from 'src/selectors/assetUploads'
import { ITEM_UPLOAD_STATUS } from 'src/services/DirectFilesUploaderCoordinator'
import { useRootStore } from 'src/new_arch/providers/RootStoreProvider'
import { queryClient } from 'src/store/store'
import { QueryFilters, QueryKeys } from 'src/new_arch/modules/projectView/queries/queryKeys'

export type { SyncedFile } from 'src/new_arch/stores/FolderSyncStore'

type FSDirectoryHandle = FileSystemDirectoryHandle & {
	values(): AsyncIterableIterator<FileSystemFileHandle | FileSystemDirectoryHandle>
}
type WindowWithFSA = {
	showDirectoryPicker( options?: { mode?: 'read' | 'readwrite' } ): Promise<FSDirectoryHandle>
}

type UseFolderSyncParams = {
	projectID: number
	folderID: number | null
}

type UseFolderSyncResult = {
	isBrowserSupported: boolean
	isWatching: boolean
	folderName: string | null
	syncedFiles: SyncedFile[]
	hasSubfolders: boolean
	startWatching: () => Promise<boolean>
	stopWatching: () => void
}

type Fingerprint = string

type TrackedEntry = {
	filename: string
	uploadID: string | number
	size: number
	lastModified: number
	subfolderPath: string
	pendingRename?: string
}

type PendingSyncedFile = { filename: string, folder_id: number }

type FileEntry = { handle: FileSystemFileHandle, relativePath: string }

type PathEntry = { handle: FileSystemFileHandle, file: File, relativePath: string }

const POLL_INTERVAL_MS = 15_000
const STABILITY_CHECK_DELAY_MS = 500

const isFolderSyncSupported = () => 'showDirectoryPicker' in window

const isAllowedExtension = ( filename: string ) => {
	const ext = filename.split( '.' ).pop()?.toLowerCase()
	return !!ext && ASSET_ALL_EXTENSIONS.includes( ext )
}

const fingerprint = ( size: number, lastModified: number ): Fingerprint => `${size}:${lastModified}`

const fileKey = ( relativePath: string, filename: string ): string => (
	relativePath ? `${relativePath}/${filename}` : filename
)

const parseFileKey = ( key: string ): { relativePath: string, filename: string } => {
	const lastSlash = key.lastIndexOf( '/' )
	if ( lastSlash < 0 ) return { relativePath: '', filename: key }
	return { relativePath: key.substring( 0, lastSlash ), filename: key.substring( lastSlash + 1 ) }
}

const collectHandles = async (
	dirHandle: FSDirectoryHandle,
	relativePath = ''
): Promise<FileEntry[]> => {
	const iter = dirHandle.values()
	const drain = async ( acc: FileEntry[] ): Promise<FileEntry[]> => {
		const { done, value } = await iter.next()
		if ( done ) return acc
		if ( value.kind === 'file' ) {
			acc.push( { handle: value as FileSystemFileHandle, relativePath } )
		} else if ( value.kind === 'directory' ) {
			const subPath = relativePath ? `${relativePath}/${value.name}` : value.name
			const subEntries = await collectHandles( value as FSDirectoryHandle, subPath )
			acc.push( ...subEntries )
		}
		return drain( acc )
	}
	return drain( [] )
}

const isFileStable = async ( handle: FileSystemFileHandle, knownSize: number ): Promise<boolean> => {
	await new Promise( resolve => setTimeout( resolve, STABILITY_CHECK_DELAY_MS ) )
	return ( await handle.getFile() ).size === knownSize
}

const getTracked = ( store: FolderSyncStore ) => store.trackedMap as Map<string | number, TrackedEntry>
const getSeenFilenames = ( store: FolderSyncStore ) => store.seenFilenamesMap as Map<string, Fingerprint | null>
const getActiveDirHandle = ( store: FolderSyncStore ) => store.activeDirHandle as FSDirectoryHandle | null
const getSubfolderVbIdMap = ( store: FolderSyncStore ) => store.subfolderVbIdMap as Map<string, number>
const getPendingSyncedFiles = ( store: FolderSyncStore ) => store.pendingSyncedFiles as PendingSyncedFile[]
type AssetUploadsMap = ReturnType<typeof getRawAssetUploads> | null
const getAssetUploads = ( store: FolderSyncStore ) => store.assetUploadsCache as AssetUploadsMap
type Api = ReturnType<typeof useApi>

type ResolveVbFolderParams = {
	relativePath: string
	rootFolderID: number | null
	projectID: number
	api: Api
	store: FolderSyncStore
	onFolderCreated?: () => void
}

const resolveVbFolderID = async ( {
	relativePath, rootFolderID, projectID, api, store, onFolderCreated
}: ResolveVbFolderParams ): Promise<number | null> => {
	if ( !relativePath ) return rootFolderID

	const vbIdMap = getSubfolderVbIdMap( store )
	const cached = vbIdMap.get( relativePath )
	if ( cached !== undefined ) return cached

	const resolveSegment = async (
		remaining: string[],
		parentID: number | null,
		accPath: string
	): Promise<number | null> => {
		if ( remaining.length === 0 ) return parentID

		const [ part, ...rest ] = remaining
		const currentPath = accPath ? `${accPath}/${part}` : part

		const cachedPart = vbIdMap.get( currentPath )
		if ( cachedPart !== undefined ) return resolveSegment( rest, cachedPart, currentPath )

		try {
			const body = parentID !== null
				? { name: part, organizer_id: parentID, organizer_type: 'Folder' }
				: { name: part, organizer_id: projectID, organizer_type: 'Project' }
			const raw = await api.post( `${FOLDERS_URL}/find_or_create`, body )
			const folder = ( raw as unknown as { response: { id: number } } ).response
			if ( typeof folder?.id !== 'number' ) return null
			// Don't use QueryClientUpdater.createdFolder — it calls addItem which adds the
			// folder unconditionally and creates duplicates when re-watching the same folder.
			// Instead just refetch, so the server always returns the deduplicated list.
			onFolderCreated?.()
			vbIdMap.set( currentPath, folder.id )

			// Lazy hydration: mark previously-synced files in this folder as already seen
			const { id } = folder
			const pending = getPendingSyncedFiles( store )
			const matching = pending.filter( f => f.folder_id === id )
			matching.forEach( ( f ) => {
				getSeenFilenames( store ).set( fileKey( currentPath, f.filename ), null )
			} )
			if ( matching.length > 0 ) {
				store.pendingSyncedFiles = pending.filter( f => f.folder_id !== id )
			}

			return resolveSegment( rest, id, currentPath )
		} catch {
			return null
		}
	}

	return resolveSegment( relativePath.split( '/' ), rootFolderID, '' )
}

const useFolderSync = ( { projectID, folderID }: UseFolderSyncParams ): UseFolderSyncResult => {
	const dispatch = useDispatch<ThunkDispatch<unknown, unknown, AnyAction>>()
	const api = useApi()
	const { folderSyncStore } = useRootStore()

	const [ isWatching, setIsWatching ] = useState( () => folderSyncStore.isWatching )
	const [ folderName, setFolderName ] = useState( () => folderSyncStore.folderName )
	const [ syncedFiles, setSyncedFiles ] = useState<SyncedFile[]>( () => [ ...folderSyncStore.syncedFiles ] )
	const [ hasSubfolders, setHasSubfolders ] = useState( () => folderSyncStore.hasSubfolders )

	useEffect( () => autorun( () => {
		setIsWatching( folderSyncStore.isWatching )
		setFolderName( folderSyncStore.folderName )
		setSyncedFiles( [ ...folderSyncStore.syncedFiles ] )
		setHasSubfolders( folderSyncStore.hasSubfolders )
	} ), [] )

	const reduxAssetUploads = useSelector( getRawAssetUploads )
	const reduxAssetUploadsRef = useRef( reduxAssetUploads )
	const isScanningRef = useRef( false )

	useEffect( () => {
		reduxAssetUploadsRef.current = reduxAssetUploads
		folderSyncStore.assetUploadsCache = reduxAssetUploads

		// On subfolder upload success: refetch assets inside the subfolder and folders
		// in the parent so the folder card shows the updated count and cover image.
		if ( !reduxAssetUploads ) return
		const vbIdMap = getSubfolderVbIdMap( folderSyncStore )
		const succeededPaths = new Set(
			Array.from( getTracked( folderSyncStore ).values() )
				.filter( e => (
					e.subfolderPath && reduxAssetUploads.get( e.uploadID )?.status === ITEM_UPLOAD_STATUS.SUCCEEDED
				) )
				.map( e => e.subfolderPath )
		)

		succeededPaths.forEach( ( subfolderPath ) => {
			const vbFolderID = vbIdMap.get( subfolderPath )
			if ( !vbFolderID ) return
			queryClient.invalidateQueries( {
				...QueryFilters.forAssetAndFolderLists( { projectID, folderID: vbFolderID, only: 'assets' } ),
				refetchType: 'active'
			} )
			const parentPath = subfolderPath.split( '/' ).slice( 0, -1 ).join( '/' )
			const parentFolderID = parentPath ? vbIdMap.get( parentPath ) ?? null : folderID ?? null
			const folderFilter = QueryFilters.forAssetAndFolderLists( { projectID, folderID: parentFolderID, only: 'folders' } )
			queryClient.invalidateQueries( { ...folderFilter, refetchType: 'none' } )
			queryClient.refetchQueries( folderFilter, { cancelRefetch: false } )
		} )
	}, [ reduxAssetUploads, folderSyncStore, projectID ] )

	const isBrowserSupported = isFolderSyncSupported()

	const scanAndUpload = useCallback( async () => {
		const activeHandle = getActiveDirHandle( folderSyncStore )
		if ( !activeHandle ) return
		const currentFolderName = activeHandle.name

		Array.from( getTracked( folderSyncStore ).entries() ).forEach( ( [ uploadID, entry ] ) => {
			const upload = getAssetUploads( folderSyncStore )?.get( uploadID )
			if ( upload?.status === ITEM_UPLOAD_STATUS.FAILED ) {
				getTracked( folderSyncStore ).delete( uploadID )
				getSeenFilenames( folderSyncStore ).delete( fileKey( entry.subfolderPath, entry.filename ) )
				folderSyncStore.updateSyncedFiles( prev => prev.filter( f => f.filename !== entry.filename ) )
				dispatch( removeAssetUploads( [ uploadID ] ) )
			}
		} )

		const allEntries = await collectHandles( activeHandle )
		const allowedEntries = allEntries.filter( e => isAllowedExtension( e.handle.name ) )

		if ( !folderSyncStore.hasSubfolders && allEntries.some( e => e.relativePath !== '' ) ) {
			folderSyncStore.setHasSubfolders( true )
		}

		// Fetch files and build all per-path maps in one pass
		const entriesWithFiles = await Promise.all(
			allowedEntries.map( async e => ( { ...e, file: await e.handle.getFile() } ) )
		)

		const byPath = new Map<string, PathEntry[]>()
		const currentByPathFilename = new Map<string, Map<string, File>>()
		const currentByPathFingerprint = new Map<string, Map<Fingerprint, File>>()
		const unmatchedByPath = new Map<string, Set<string>>()

		entriesWithFiles.forEach( ( entry ) => {
			const { relativePath, file } = entry

			const pathArr = byPath.get( relativePath ) ?? []
			pathArr.push( entry )
			byPath.set( relativePath, pathArr )

			const byFilename = currentByPathFilename.get( relativePath ) ?? new Map<string, File>()
			byFilename.set( file.name, file )
			currentByPathFilename.set( relativePath, byFilename )

			const byFP = currentByPathFingerprint.get( relativePath ) ?? new Map<Fingerprint, File>()
			byFP.set( fingerprint( file.size, file.lastModified ), file )
			currentByPathFingerprint.set( relativePath, byFP )

			const unmatched = unmatchedByPath.get( relativePath ) ?? new Set<string>()
			unmatched.add( file.name )
			unmatchedByPath.set( relativePath, unmatched )
		} )

		const trackedKeys = new Set(
			Array.from( getTracked( folderSyncStore ).values() )
				.map( e => fileKey( e.subfolderPath, e.filename ) )
		)

		getSeenFilenames( folderSyncStore ).forEach( ( seenFP, key ) => {
			if ( trackedKeys.has( key ) ) return
			const { relativePath, filename } = parseFileKey( key )
			const current = currentByPathFilename.get( relativePath )?.get( filename )
			if ( !current ) return
			const currentFP = fingerprint( current.size, current.lastModified )
			if ( seenFP === null ) {
				getSeenFilenames( folderSyncStore ).set( key, currentFP )
			} else if ( currentFP !== seenFP ) {
				getSeenFilenames( folderSyncStore ).delete( key )
			}
		} )

		const usedFingerprints = new Set<Fingerprint>()

		// Process tracked uploads — updates unmatchedByPath as tracked files are matched
		Array.from( getTracked( folderSyncStore ).entries() ).forEach( ( [ uploadID, entry ] ) => {
			const { subfolderPath, filename } = entry
			const fp = fingerprint( entry.size, entry.lastModified )
			const byFilename = currentByPathFilename.get( subfolderPath )?.get( filename )

			if ( byFilename ) {
				if ( byFilename.lastModified !== entry.lastModified ) {
					getTracked( folderSyncStore ).delete( uploadID )
					getSeenFilenames( folderSyncStore ).delete( fileKey( subfolderPath, filename ) )
				} else {
					unmatchedByPath.get( subfolderPath )?.delete( filename )
				}
			} else {
				const byFP = currentByPathFingerprint.get( subfolderPath )?.get( fp )
				const unmatchedInPath = unmatchedByPath.get( subfolderPath )
				// Rename detection is scoped within the same subfolder path. A file moved between
				// subfolders is treated as delete + new upload — cross-path renames are not detected.
				const isRename = byFP && unmatchedInPath?.has( byFP.name ) && !usedFingerprints.has( fp )

				if ( isRename ) {
					const newFilename = byFP.name
					usedFingerprints.add( fp )
					unmatchedInPath?.delete( newFilename )
					getTracked( folderSyncStore ).set( uploadID, {
						...entry, filename: newFilename, pendingRename: newFilename
					} )
					dispatch( updateAssetUploadFile( uploadID, byFP ) )
					folderSyncStore.updateSyncedFiles( prev => prev.map(
						f => ( f.filename === filename ? { ...f, filename: newFilename } : f )
					) )
					getSeenFilenames( folderSyncStore ).set(
						fileKey( subfolderPath, newFilename ),
						fingerprint( byFP.size, byFP.lastModified )
					)
					const renameVbFolderID = subfolderPath
						? getSubfolderVbIdMap( folderSyncStore ).get( subfolderPath ) ?? folderID ?? 0
						: folderID ?? 0
					api.post(
						`${PROJECTS_URL}/${projectID}/folder_sync/register`,
						{ folder_name: currentFolderName, folder_id: renameVbFolderID, filenames: [ newFilename ] }
					).catch( () => { /* non-critical */ } )
				} else {
					getTracked( folderSyncStore ).delete( uploadID )
					dispatch( removeAssetUploads( [ uploadID ] ) )
					folderSyncStore.updateSyncedFiles( prev => prev.filter( f => f.filename !== filename ) )
				}
			}
		} )

		let newFoldersCreated = false

		const processPathGroup = async (
			relativePath: string,
			unmatchedFilenames: Set<string>,
			pathEntries: PathEntry[]
		): Promise<void> => {
			// Also hydrates seenFilenamesMap with previously-synced files for this folder.
			const vbFolderID = await resolveVbFolderID( {
				relativePath,
				rootFolderID: folderID,
				projectID,
				api,
				store: folderSyncStore,
				onFolderCreated: () => { newFoldersCreated = true }
			} )

			// If folder creation failed for a subfolder, skip and retry on next cycle
			if ( vbFolderID === null && relativePath !== '' ) return

			const newHandles = pathEntries.filter(
				e => (
					unmatchedFilenames.has( e.file.name )
					&& !getSeenFilenames( folderSyncStore ).has( fileKey( relativePath, e.file.name ) )
				)
			)

			if ( newHandles.length === 0 ) return

			const stableFiles = await Promise.all(
				newHandles.map( async ( { handle, file } ) => (
					await isFileStable( handle, file.size ) ? file : null
				) )
			).then( results => results.filter( Boolean ) as File[] )

			if ( stableFiles.length === 0 ) return

			const matchDispatch = dispatch(
				checkAssetFilenamesMatches( {
					projectID, folderID: vbFolderID, files: stableFiles, localFolderName: currentFolderName
				} )
			)
			const matchAction = await ( matchDispatch as Promise<{ payload: FilenamesMatchResult }> )
			const newRounds = ( matchAction?.payload?.exactMatches ?? [] ).map(
				m => ( { filename: m.filename, assetID: m.match.id } )
			)

			const action = dispatch( createFileUploads( {
				projectID,
				folderID: vbFolderID,
				parentUploadID: undefined,
				files: stableFiles,
				newRounds,
				localFolderName: currentFolderName
			} ) )
			const payload: Array<{ id: string | number }> = action?.payload ?? []

			stableFiles.forEach( ( file, index ) => {
				const uploadID = payload[ index ]?.id
				if ( uploadID == null ) return
				getTracked( folderSyncStore ).set( uploadID, {
					filename: file.name,
					uploadID,
					size: file.size,
					lastModified: file.lastModified,
					subfolderPath: relativePath
				} )
				folderSyncStore.updateSyncedFiles( prev => [
					...prev, { filename: file.name, detectedAt: new Date() }
				] )
			} )

			await api.post(
				`${PROJECTS_URL}/${projectID}/folder_sync/register`,
				{
					folder_name: currentFolderName,
					folder_id: vbFolderID ?? 0,
					filenames: stableFiles.map( f => f.name )
				}
			).then( () => {
				stableFiles.forEach( ( file ) => {
					getSeenFilenames( folderSyncStore ).set(
						fileKey( relativePath, file.name ),
						fingerprint( file.size, file.lastModified )
					)
				} )
				// Prime the assets query so it has data when the upload batch completes.
				queryClient.invalidateQueries( {
					...QueryFilters.forAssetAndFolderLists( {
						projectID, folderID: vbFolderID, only: 'assets'
					} ),
					refetchType: 'active'
				} )
			} ).catch( () => { /* non-critical */ } )
		}

		await Array.from( byPath.entries() ).reduce<Promise<void>>(
			( chain, [ relativePath, pathEntries ] ) => (
				chain.then( () => processPathGroup(
					relativePath,
					unmatchedByPath.get( relativePath ) ?? new Set(),
					pathEntries
				) )
			),
			Promise.resolve()
		)

		if ( newFoldersCreated ) {
			const folderListFilter = QueryFilters.forAssetAndFolderLists( { projectID, only: 'folders' } )
			const folderTreeFilter = { queryKey: QueryKeys.forFolderTree( projectID ) }
			queryClient.invalidateQueries( { ...folderListFilter, refetchType: 'none' } )
			queryClient.invalidateQueries( { ...folderTreeFilter, refetchType: 'none' } )
			queryClient.refetchQueries( folderListFilter, { cancelRefetch: false } )
			queryClient.refetchQueries( folderTreeFilter, { cancelRefetch: false } )
		}

		Array.from( getTracked( folderSyncStore ).entries() )
			.filter( ( [ , entry ] ) => !!entry.pendingRename )
			.forEach( ( [ uploadID, entry ] ) => {
				( dispatch( renameUploadedAsset( uploadID, entry.pendingRename! ) ) as Promise<boolean> )
					.then( ( renamed ) => {
						if ( !renamed ) return
						const current = getTracked( folderSyncStore ).get( uploadID )
						if ( current ) {
							getTracked( folderSyncStore ).set( uploadID, { ...current, pendingRename: undefined } )
						}
					} )
			} )
	}, [ dispatch, folderSyncStore, projectID, folderID, api ] )

	const guardedScanAndUpload = useCallback( async () => {
		if ( isScanningRef.current ) return
		isScanningRef.current = true
		try {
			await scanAndUpload()
		} finally {
			isScanningRef.current = false
		}
	}, [ scanAndUpload ] )

	const clearTrackedUploads = useCallback( () => {
		if ( getTracked( folderSyncStore ).size === 0 ) return
		const ids = [ ...getTracked( folderSyncStore ).values() ].map( e => e.uploadID )
		dispatch( removeAssetUploads( ids ) )
		folderSyncStore.trackedMap = new Map()
	}, [ dispatch, folderSyncStore ] )

	const stopWatching = useCallback( () => {
		if ( folderSyncStore.activeDirHandle ) {
			trackAutoSyncFolderStopped( { assetsSynced: folderSyncStore.syncedFiles.length } )
		}
		clearTrackedUploads()
		if ( folderSyncStore.intervalHandle ) {
			clearInterval( folderSyncStore.intervalHandle )
			folderSyncStore.intervalHandle = null
		}
		folderSyncStore.activeDirHandle = null
		folderSyncStore.seenFilenamesMap = new Map()
		folderSyncStore.subfolderVbIdMap = new Map()
		folderSyncStore.pendingSyncedFiles = []
		folderSyncStore.setIsWatching( false )
		folderSyncStore.setFolderName( null )
		folderSyncStore.setSyncedFiles( [] )
		folderSyncStore.setHasSubfolders( false )
	}, [ clearTrackedUploads, folderSyncStore ] )

	const startWatching = useCallback( async (): Promise<boolean> => {
		if ( !isFolderSyncSupported() ) return false

		try {
			if ( folderSyncStore.intervalHandle ) {
				clearInterval( folderSyncStore.intervalHandle )
				folderSyncStore.intervalHandle = null
			}
			clearTrackedUploads()

			const handle = await ( window as unknown as WindowWithFSA ).showDirectoryPicker( { mode: 'read' } )
			folderSyncStore.activeDirHandle = handle
			folderSyncStore.trackedMap = new Map()
			folderSyncStore.seenFilenamesMap = new Map()
			folderSyncStore.subfolderVbIdMap = new Map()
			folderSyncStore.pendingSyncedFiles = []
			folderSyncStore.setSyncedFiles( [] )
			folderSyncStore.setFolderName( handle.name )
			folderSyncStore.setIsWatching( true )
			folderSyncStore.setHasSubfolders( false )
			trackWatchFolderSelected( { folderName: handle.name } )

			try {
				const response = await api.get( `${PROJECTS_URL}/${projectID}/folder_sync` )
				const { synced_files = [] } = response as unknown as {
					synced_files: Array<{ filename: string, folder_id: number }>
				}
				const rootFolderIdValue = folderID ?? 0
				synced_files
					.filter( f => f.folder_id === rootFolderIdValue )
					.forEach( ( f ) => {
						folderSyncStore.seenFilenamesMap.set( fileKey( '', f.filename ), null )
					} )
				folderSyncStore.pendingSyncedFiles = synced_files.filter( f => f.folder_id !== rootFolderIdValue )
			} catch {
				folderSyncStore.seenFilenamesMap = new Map()
				folderSyncStore.pendingSyncedFiles = []
			}

			await guardedScanAndUpload()
			folderSyncStore.intervalHandle = setInterval( guardedScanAndUpload, POLL_INTERVAL_MS )
			return true
		} catch ( err ) {
			if ( !( err instanceof DOMException && err.name === 'AbortError' ) ) {
				throw err
			}
			return false
		}
	}, [ guardedScanAndUpload, clearTrackedUploads, api, projectID, folderID, folderSyncStore ] )

	useEffect( () => {
		if ( !isWatching ) return () => {}
		const handleBeforeUnload = ( e: BeforeUnloadEvent ) => {
			e.preventDefault()
			e.returnValue = ''
		}
		window.addEventListener( 'beforeunload', handleBeforeUnload )
		return () => window.removeEventListener( 'beforeunload', handleBeforeUnload )
	}, [ isWatching ] )

	useEffect( () => () => {
		if ( folderSyncStore.isWatching ) return
		if ( getTracked( folderSyncStore ).size > 0 ) {
			const ids = [ ...getTracked( folderSyncStore ).values() ].map( e => e.uploadID )
			dispatch( removeAssetUploads( ids ) )
		}
	}, [] )

	return {
		isBrowserSupported,
		isWatching,
		folderName,
		syncedFiles,
		hasSubfolders,
		startWatching,
		stopWatching
	}
}

export default useFolderSync
